import { useState, useEffect } from "react";
import { certificateService } from "../../services/certificateService";
import { Button } from "../ui/Button";
import { Award, Check, X, Eye } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

interface CertificateRequestsTableProps {
  studentId: string;
  onRefresh?: () => void;
}

export function CertificateRequestsTable({
  studentId,
  onRefresh,
}: CertificateRequestsTableProps) {
  const { hasPermission } = useAuth();

  const canApprove = hasPermission('certificates:approve');
  const canReject = hasPermission('certificates:reject');
  const canRevoke = hasPermission('certificates:revoke');

  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRequests = async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const response =
        await certificateService.getStudentCertificateRequests(studentId);
      setRequests(response.requests || []);
    } catch (error) {
      console.error("Failed to fetch certificate requests:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [studentId]);

  const handleApprove = async (requestId: string) => {
    if (
      !confirm("Are you sure you want to approve this certificate request?")
    ) {
      return;
    }

    try {
      await certificateService.approveRequest(requestId);
      alert("Certificate request approved successfully!");
      fetchRequests();
      if (onRefresh) onRefresh();
    } catch (error: any) {
      console.error("Failed to approve request:", error);
      alert(
        "Failed to approve request: " +
          (error.response?.data?.message || error.message),
      );
    }
  };

  const handleReject = async (requestId: string) => {
    const reason = prompt("Please enter the reason for rejection:");
    if (!reason) return;

    try {
      await certificateService.rejectRequest(requestId, reason);
      alert("Certificate request rejected successfully!");
      fetchRequests();
      if (onRefresh) onRefresh();
    } catch (error: any) {
      console.error("Failed to reject request:", error);
      alert(
        "Failed to reject request: " +
          (error.response?.data?.message || error.message),
      );
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${
          colors[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Award className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Certificate Requests</h3>
        </div>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Award className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Certificate Requests</h3>
        </div>
        <div className="text-center py-8 text-gray-500">
          <Award className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p>No certificate requests found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Award className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Certificate Requests</h3>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-[1000px] w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                Course
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                Requested Name
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                Reason
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                Status
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                Request Date
              </th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => (
              <tr
                key={request._id}
                className="border-b border-gray-100 hover:bg-gray-50"
              >
                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                  {request.course?.title || "N/A"}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {request.requestedName || "N/A"}
                </td>
                <td
                  className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate"
                  title={request.reason}
                >
                  {request.reason || "N/A"}
                </td>
                <td className="px-4 py-3 text-sm">
                  {getStatusBadge(request.status)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {request.createdAt
                    ? new Date(request.createdAt).toLocaleDateString()
                    : "N/A"}
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <div className="flex justify-end gap-2">
                    {request.status === "pending" && (
                      <>
                        {canApprove && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => handleApprove(request._id)}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                        )}
                        {canReject && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleReject(request._id)}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        )}
                      </>
                    )}
                    {request.status === "approved" && request.certificateId && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          // Navigate to certificate or show details
                          alert(`Certificate ID: ${request.certificateId}`);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View Certificate
                      </Button>
                    )}
                    {request.status === "rejected" && request.adminNote && (
                      <div
                        className="text-sm text-gray-500"
                        title={request.adminNote}
                      >
                        Admin Note: {request.adminNote.substring(0, 30)}...
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
